import React, { useState } from 'react'
import {
  Brain, ChevronRight, CheckCircle, XCircle,
  RotateCcw, MessageCircle, Target,
  AlertTriangle, BookOpen, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react'
import { useUnitProgress } from '../store/progressStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'A1-2'

interface TutorScenario {
  id: string
  label: string
  emoji: string
  category: string
  learnStepId: string
  question: string
  correctAnswer: string
  wrongAnswer: string
  misconceptionCode: string
  note: string
  analysis: {
    diagnosis: string
    rootCause: string
    correction: string
    example: string
  }
  followUps: Array<{ q: string; a: string }>
}

const DEMO_SCENARIOS: TutorScenario[] = [
  {
    id: 'S1',
    label: '시나리오 1',
    emoji: '⚖️',
    category: '등식의 성질',
    learnStepId: 'AB02',
    question: '등식 x+5=12에서 양변에서 5를 빼면?',
    correctAnswer: 'x=7 (양변에서 5를 빼면 x+5-5=12-5 → x=7)',
    wrongAnswer: 'x=17 (양변에 5를 더함)',
    misconceptionCode: 'BE01',
    note: '등식의 성질: 이항 방향과 부호 변화를 혼동',
    analysis: {
      diagnosis: '학생이 x+5=12에서 x=17로 계산했습니다. 5를 빼는 대신 더한 오류입니다.',
      rootCause: '등식의 성질을 적용할 때 "양변에서 같은 수를 빼야" 등호가 유지되는데, 반대로 더했습니다.',
      correction: '등식의 성질 규칙:\n등호 양변에 같은 수를 더하거나 빼도 등식 성립\n\nx+5=12\n→ 양변에서 5를 빼면\nx+5-5 = 12-5\nx = 7',
      example: 'x+3=10 → x=10-3=7\nx-4=6 → x=6+4=10\n2+x=9 → x=9-2=7',
    },
    followUps: [
      { q: 'x-8=3을 풀면?', a: 'x=3+8=11 (양변에 8을 더함)' },
      { q: '왜 양변에 같은 것을 해야 하나요?', a: '저울처럼! 양쪽에 같은 무게를 올리거나 내려야 균형이 유지됩니다.' },
    ],
  },
  {
    id: 'S2',
    label: '시나리오 2',
    emoji: '🔄',
    category: '이항',
    learnStepId: 'AB03',
    question: '2x+3=9에서 3을 이항하면?',
    correctAnswer: '2x=9-3=6 (이항하면 부호 반전)',
    wrongAnswer: '2x=9+3=12 (이항해도 부호 그대로)',
    misconceptionCode: 'BE02',
    note: '이항 시 부호가 반전됨을 모르는 오류',
    analysis: {
      diagnosis: '학생이 2x+3=9에서 3을 이항하여 2x=12로 계산했습니다. 이항 시 부호 변화를 적용하지 않은 오류입니다.',
      rootCause: '이항이란 한 변의 항을 다른 변으로 옮기는 것으로, 이때 부호가 반드시 반전(+→-, -→+)됩니다.',
      correction: '이항 규칙: 부호 반전!\n\n2x+3=9\n→ +3을 우변으로 이항 → -3\n2x = 9-3\n2x = 6\nx = 3',
      example: '3x-5=7 → 3x=7+5=12 → x=4\nx+6=10 → x=10-6=4\n4x-1=11 → 4x=11+1=12 → x=3',
    },
    followUps: [
      { q: '5x-2=13에서 -2를 이항하면?', a: '5x=13+2=15 (이항하면 -→+로 부호 반전)' },
      { q: '이항과 등식의 성질은 같은 건가요?', a: '결과는 같습니다! 이항은 등식의 성질(양변에 같은 수 더하기/빼기)을 간단히 표현한 방법입니다.' },
    ],
  },
  {
    id: 'S3',
    label: '시나리오 3',
    emoji: '✖️',
    category: '계수로 나누기',
    learnStepId: 'AB03',
    question: '3x=12에서 x의 값은?',
    correctAnswer: 'x=4 (양변을 3으로 나눔)',
    wrongAnswer: 'x=36 (양변에 3을 곱함)',
    misconceptionCode: 'BE03',
    note: '계수 처리 시 나눗셈 대신 곱셈을 사용하는 오류',
    analysis: {
      diagnosis: '학생이 3x=12에서 x=36으로 계산했습니다. 계수 3을 없애려면 나눠야 하는데 곱한 오류입니다.',
      rootCause: '3x에서 계수 3을 없애려면 등식의 성질을 이용해 양변을 3으로 나눠야 합니다.',
      correction: '계수 없애기: 양변을 계수로 나누기\n\n3x = 12\n→ 양변을 3으로 나누면\n3x÷3 = 12÷3\nx = 4',
      example: '5x=20 → x=20÷5=4\n2x=14 → x=14÷2=7\n-4x=16 → x=16÷(-4)=-4',
    },
    followUps: [
      { q: '-2x=10이면 x는?', a: 'x=10÷(-2)=-5 (음수 계수에 주의!)' },
      { q: '½x=3이면 x는?', a: '양변에 2를 곱함: x=6 (분수 계수는 역수 곱하기)' },
    ],
  },
  {
    id: 'S4',
    label: '시나리오 4',
    emoji: '📐',
    category: '괄호 있는 방정식',
    learnStepId: 'AB03',
    question: '2(x+3)=10을 풀면?',
    correctAnswer: 'x=2 (분배: 2x+6=10 → 2x=4 → x=2)',
    wrongAnswer: 'x=2 → x+3=10 → x=7 (나누기 전에 3을 뺀 오류)',
    misconceptionCode: 'BE04',
    note: '분배법칙을 먼저 적용하지 않고 괄호 안을 직접 처리하는 오류',
    analysis: {
      diagnosis: '학생이 2(x+3)=10에서 먼저 2로 나눠 x+3=5로 처리했지만, 분배법칙을 먼저 적용해야 합니다.',
      rootCause: '2(x+3)=10을 풀 때는 분배법칙을 먼저 적용하거나, 양변을 2로 나눈 뒤 이항해야 합니다.',
      correction: '방법1 - 분배법칙 먼저:\n2(x+3) = 10\n2x+6 = 10\n2x = 10-6 = 4\nx = 2\n\n방법2 - 양변을 2로 먼저 나누기:\n2(x+3) = 10\nx+3 = 5\nx = 2',
      example: '3(x-2)=9 → 3x-6=9 → 3x=15 → x=5\n2(2x+1)=14 → 4x+2=14 → 4x=12 → x=3',
    },
    followUps: [
      { q: '4(x-1)=12를 풀면?', a: '4x-4=12 → 4x=16 → x=4' },
      { q: '-3(x+2)=6을 풀면?', a: '-3x-6=6 → -3x=12 → x=-4' },
    ],
  },
  {
    id: 'S5',
    label: '시나리오 5',
    emoji: '🧩',
    category: '활용 문제 세우기',
    learnStepId: 'AB04',
    question: '어떤 수의 3배에서 5를 빼면 16이다. 방정식을 세우면?',
    correctAnswer: '3x-5=16 (어떤 수를 x로 놓고 식 표현)',
    wrongAnswer: '3x+5=16 (빼는 것을 더하기로 표현)',
    misconceptionCode: 'BE05',
    note: '문제의 "빼다"를 방정식에서 부호 반대로 표현하는 오류',
    analysis: {
      diagnosis: '학생이 "어떤 수의 3배에서 5를 빼면 16"을 3x+5=16으로 세웠습니다. "빼다"를 +로 표현한 오류입니다.',
      rootCause: '"3배에서 5를 빼다" → 3x-5. "에서 빼다"는 빼기(-)를 의미합니다.',
      correction: '활용 문제 방정식 세우기:\n① 미지수 정하기: 어떤 수 = x\n② 조건 번역: "3배에서 5를 빼면" → 3x-5\n③ 등호로 연결: "16이다" → =16\n④ 방정식: 3x-5=16',
      example: '"어떤 수의 2배에 3을 더하면 11"\n→ 2x+3=11\n\n"어떤 수에서 4를 빼면 수의 3배"\n→ x-4=3x → -4=2x → x=-2',
    },
    followUps: [
      { q: '"어떤 수의 4배는 어떤 수보다 9만큼 크다"의 방정식은?', a: '4x=x+9 (또는 4x-x=9 → 3x=9 → x=3)' },
      { q: '방정식 풀기: 3x-5=16', a: '3x=21 → x=7' },
    ],
  },
]

// ── 서브 컴포넌트 ────────────────────────────────────────────────────────────
function FragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const visible = fragments.slice(0, 2)
  if (visible.length === 0) return null
  return (
    <div className="card border border-amber-200 bg-amber-50 mb-4">
      <p className="text-xs font-bold text-amber-700 mb-2">⚡ AI 예상 취약 개념</p>
      <div className="space-y-1.5">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <span className={`text-xs font-semibold flex-1 ${cfg.color}`}>{f.fragment.title}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function FollowUpCard({ followUps }: { followUps: TutorScenario['followUps'] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null)
  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-bold text-amber-600 mb-1">💬 추가 질문</p>
      {followUps.map((fu, i) => (
        <div key={i} className="border border-amber-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setOpenIdx(openIdx === i ? null : i)}
            className="w-full flex items-center justify-between px-3 py-2 bg-amber-50 hover:bg-amber-100 text-left transition-colors"
          >
            <span className="text-xs text-slate-700 font-medium flex-1">{fu.q}</span>
            {openIdx === i
              ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0" />
              : <ChevronDown size={13} className="text-slate-400 flex-shrink-0" />}
          </button>
          {openIdx === i && (
            <div className="px-3 py-2 bg-white">
              <p className="text-xs text-amber-700 font-semibold whitespace-pre-line">{fu.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
export default function A1_2TutorPage() {
  const [selectedId, setSelectedId] = useState<string>('S1')
  const [step, setStep] = useState<'question' | 'wrong' | 'analysis' | 'correct'>('question')
  const [showNote, setShowNote] = useState(false)

  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const scenario = DEMO_SCENARIOS.find(s => s.id === selectedId)!

  const reset = () => { setStep('question'); setShowNote(false) }
  const handleSelect = (id: string) => { setSelectedId(id); reset() }

  return (
    <div className="p-4 space-y-4">
      <FragmentPanel fragments={predictedFragments} />

      {/* 헤더 */}
      <div className="card bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <Brain size={20} className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-base font-black text-slate-800">AI 튜터 — 일차방정식</h2>
            <p className="text-xs text-slate-500">A1-2 오개념 분석 · 맞춤 피드백</p>
          </div>
        </div>
      </div>

      {/* 시나리오 선택 */}
      <div>
        <p className="text-xs font-bold text-slate-500 mb-2 px-1">📋 학습 시나리오 선택</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {DEMO_SCENARIOS.map(s => (
            <button key={s.id} onClick={() => handleSelect(s.id)}
              className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                selectedId === s.id
                  ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-amber-50 hover:border-amber-200'
              }`}>
              {s.emoji} {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 시나리오 카드 */}
      <div className="card border-2 border-amber-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold">
            {scenario.category}
          </span>
          <span className="text-xs text-slate-400">{scenario.learnStepId}</span>
        </div>

        {step === 'question' && (
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <p className="text-xs font-bold text-slate-500 mb-1 flex items-center gap-1">
                <MessageCircle size={11} /> 학생 질문
              </p>
              <p className="text-sm font-semibold text-slate-800">{scenario.question}</p>
            </div>
            <p className="text-xs text-slate-500 text-center">학생의 답을 먼저 예상해보세요.</p>
            <button onClick={() => setStep('wrong')}
              className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
              <ChevronRight size={15} /> 학생 답 확인
            </button>
          </div>
        )}

        {step === 'wrong' && (
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-xs font-bold text-slate-500 mb-1">📝 문제</p>
              <p className="text-sm text-slate-700">{scenario.question}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-3 border border-red-200">
              <p className="text-xs font-bold text-red-500 mb-1 flex items-center gap-1">
                <XCircle size={11} /> 학생 오답
              </p>
              <p className="text-sm font-bold text-red-700">{scenario.wrongAnswer}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowNote(!showNote)}
                className="flex-1 py-2 bg-amber-100 text-amber-700 rounded-xl text-xs font-semibold hover:bg-amber-200 transition-colors flex items-center justify-center gap-1">
                <AlertTriangle size={12} /> 오개념 메모
              </button>
              <button onClick={() => setStep('analysis')}
                className="flex-1 py-2 bg-amber-500 text-white rounded-xl text-xs font-bold hover:bg-amber-600 transition-colors flex items-center justify-center gap-1">
                <Brain size={12} /> AI 분석
              </button>
            </div>
            {showNote && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs font-bold text-amber-700 mb-1">⚠️ 오개념 [{scenario.misconceptionCode}]</p>
                <p className="text-xs text-amber-800">{scenario.note}</p>
              </div>
            )}
          </div>
        )}

        {step === 'analysis' && (
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs font-bold text-amber-600 mb-2 flex items-center gap-1">
                <Sparkles size={11} /> AI 진단
              </p>
              <p className="text-xs text-amber-800">{scenario.analysis.diagnosis}</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
              <p className="text-xs font-bold text-orange-600 mb-1">🔍 근본 원인</p>
              <p className="text-xs text-orange-800">{scenario.analysis.rootCause}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-xs font-bold text-blue-600 mb-1">🛠️ 교정 방법</p>
              <p className="text-xs text-blue-800 whitespace-pre-line">{scenario.analysis.correction}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <p className="text-xs font-bold text-slate-600 mb-1">📌 예시</p>
              <p className="text-xs text-slate-700 font-mono whitespace-pre-line">{scenario.analysis.example}</p>
            </div>
            <button onClick={() => setStep('correct')}
              className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
              <CheckCircle size={15} /> 정답 및 후속 학습
            </button>
          </div>
        )}

        {step === 'correct' && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <p className="text-xs font-bold text-emerald-600 mb-1 flex items-center gap-1">
                <CheckCircle size={11} /> 정답
              </p>
              <p className="text-sm font-bold text-emerald-700 whitespace-pre-line">{scenario.correctAnswer}</p>
            </div>
            <FollowUpCard followUps={scenario.followUps} />
            <div className="flex gap-2 mt-2">
              <button onClick={reset}
                className="flex-1 py-2 border border-slate-200 rounded-xl text-xs text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1">
                <RotateCcw size={12} /> 다시 보기
              </button>
              <button onClick={() => {
                const keys = DEMO_SCENARIOS.map(s => s.id)
                const next = keys[(keys.indexOf(selectedId) + 1) % keys.length]
                handleSelect(next)
              }}
                className="flex-1 py-2 bg-amber-500 text-white rounded-xl text-xs font-bold hover:bg-amber-600 flex items-center justify-center gap-1">
                <ChevronRight size={12} /> 다음 시나리오
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 진행 표시 */}
      <div className="flex items-center justify-center gap-2">
        {(['question', 'wrong', 'analysis', 'correct'] as const).map(s => (
          <div key={s} className={`h-1.5 rounded-full transition-all ${s === step ? 'w-8 bg-amber-500' : 'w-3 bg-slate-200'}`} />
        ))}
      </div>

      {/* 핵심 오개념 정리 */}
      <div className="card border border-slate-200">
        <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
          <Target size={14} className="text-amber-500" /> 핵심 오개념 정리
        </h3>
        <div className="space-y-2">
          {[
            { code: 'BE01', text: '등식의 성질: 양변에 같은 수를 더하거나 빼도 등호 유지' },
            { code: 'BE02', text: '이항 시 부호 반전 — +3 이항 → -3 (반대 부호)' },
            { code: 'BE03', text: '계수 없애기: 양변을 계수로 나누기 — 3x=12 → x=4' },
            { code: 'BE04', text: '괄호 있는 방정식: 분배법칙 먼저 또는 양변을 계수로 나누기' },
            { code: 'BE05', text: '활용 문제: 미지수 x 설정 → 조건을 등식으로 번역' },
            { code: 'BE06', text: '검산 필수: 구한 x를 원래 방정식에 대입해 확인' },
          ].map(item => (
            <div key={item.code} className="flex gap-2 items-start bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              <span className="text-xs font-black text-red-400 flex-shrink-0 mt-0.5">{item.code}</span>
              <p className="text-xs text-red-800">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 학습 링크 */}
      <div className="card border border-amber-100 bg-amber-50">
        <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
          <BookOpen size={12} /> 관련 학습 단계
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { id: 'AB01', title: '방정식과 해의 개념' },
            { id: 'AB02', title: '등식의 성질' },
            { id: 'AB03', title: '이항과 풀이' },
            { id: 'AB04', title: '활용 (거리·속력·시간)' },
            { id: 'AB05', title: '활용 (농도·비율)' },
          ].map(s => (
            <div key={s.id} className="bg-white rounded-xl px-3 py-2 border border-amber-100">
              <p className="text-xs font-bold text-amber-600">{s.id}</p>
              <p className="text-xs text-slate-600">{s.title}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
